import Phaser from 'phaser';
import { GameEntity } from '../socket/SocketManager';
import { IsoMap } from './IsoMap';

const SHIRT_COLORS = [
  0xcc3333, 0x3366cc, 0x33cc33, 0xcccc33, 0xcc33cc,
  0x33cccc, 0xff6633, 0x6633ff, 0x339966, 0x993366,
];

export class IsoPedestrian {
  private graphics: Phaser.GameObjects.Graphics;
  private currentX: number;
  private currentY: number;
  private previousX: number;
  private previousY: number;
  private targetX: number;
  private targetY: number;
  private skinColor: number;
  private shirtColor: number;
  private direction: string = 'S';
  private state: string = 'walking';
  private animTick = 0;

  constructor(
    private scene: Phaser.Scene,
    entity: GameEntity,
    private isoMap: IsoMap,
  ) {
    this.graphics = scene.add.graphics();

    this.skinColor = entity.color || 0xe8b88a;
    const hash = entity.id.split('').reduce((a, c) => a + c.charCodeAt(0), 0);
    this.shirtColor = SHIRT_COLORS[hash % SHIRT_COLORS.length];
    this.direction = entity.direction || 'S';
    this.state = entity.state || 'walking';

    const pos = isoMap.gridToIso(entity.gridX, entity.gridY);
    this.currentX = pos.x;
    this.currentY = pos.y;
    this.previousX = pos.x;
    this.previousY = pos.y;
    this.targetX = pos.x;
    this.targetY = pos.y;

    this.draw();
  }

  updateTarget(entity: GameEntity): void {
    this.previousX = this.currentX;
    this.previousY = this.currentY;
    this.direction = entity.direction || this.direction;
    this.state = entity.state || this.state;

    const pos = this.isoMap.gridToIso(entity.gridX, entity.gridY);
    this.targetX = pos.x;
    this.targetY = pos.y;
  }

  interpolate(t: number): void {
    this.animTick++;

    if (this.state === 'idle') {
      this.draw();
      return;
    }

    // Crossing speed boost
    const speedMult = this.state === 'crossing' ? 1.3 : 1.0;
    const adjustedT = Math.min(t * speedMult, 1);

    this.currentX = this.previousX + (this.targetX - this.previousX) * adjustedT;
    this.currentY = this.previousY + (this.targetY - this.previousY) * adjustedT;
    this.draw();
  }

  private draw(): void {
    this.graphics.clear();
    const cx = this.currentX;
    let cy = this.currentY;

    const approxGrid = cy / 16 + cx / 32;
    this.graphics.setDepth(approxGrid + 0.1);

    // Walking bob animation
    let bobOffset = 0;
    if (this.state === 'walking' || this.state === 'crossing') {
      bobOffset = Math.sin(this.animTick * 0.3) * 1.5;
    }
    cy += bobOffset;

    // Shadow
    this.graphics.fillStyle(0x000000, 0.2);
    this.graphics.fillEllipse(this.currentX, this.currentY + 2, 8, 4);

    // Legs (two small lines)
    if (this.state !== 'idle') {
      const legOffset = Math.sin(this.animTick * 0.3) * 2;
      this.graphics.fillStyle(0x333366, 1);
      this.graphics.fillRect(cx - 2 + legOffset, cy - 1, 2, 5);
      this.graphics.fillRect(cx + 1 - legOffset, cy - 1, 2, 5);
    } else {
      this.graphics.fillStyle(0x333366, 1);
      this.graphics.fillRect(cx - 2, cy - 1, 2, 5);
      this.graphics.fillRect(cx + 1, cy - 1, 2, 5);
    }

    // Body (shirt)
    this.graphics.fillStyle(this.shirtColor, 1);
    this.graphics.fillEllipse(cx, cy - 5, 7, 9);

    // Arms
    if (this.state === 'walking' || this.state === 'crossing') {
      const armSwing = Math.sin(this.animTick * 0.3) * 1.5;
      this.graphics.fillStyle(this.skinColor, 1);
      this.graphics.fillCircle(cx - 4, cy - 4 + armSwing, 1.5);
      this.graphics.fillCircle(cx + 4, cy - 4 - armSwing, 1.5);
    }

    // Head
    this.graphics.fillStyle(this.skinColor, 1);
    this.graphics.fillCircle(cx, cy - 12, 3.5);

    // Direction indicator (tiny eyes)
    this.graphics.fillStyle(0x222222, 1);
    switch (this.direction) {
      case 'N':
        this.graphics.fillCircle(cx - 1, cy - 13, 0.5);
        this.graphics.fillCircle(cx + 1, cy - 13, 0.5);
        break;
      case 'S':
        this.graphics.fillCircle(cx - 1, cy - 11, 0.5);
        this.graphics.fillCircle(cx + 1, cy - 11, 0.5);
        break;
      case 'E':
        this.graphics.fillCircle(cx + 1, cy - 12.5, 0.5);
        this.graphics.fillCircle(cx + 1, cy - 11.5, 0.5);
        break;
      case 'W':
        this.graphics.fillCircle(cx - 1, cy - 12.5, 0.5);
        this.graphics.fillCircle(cx - 1, cy - 11.5, 0.5);
        break;
    }
  }

  getGraphics(): Phaser.GameObjects.Graphics {
    return this.graphics;
  }

  destroy(): void {
    this.graphics.destroy();
  }
}
